import { CasClient } from '@central-products/cas-client';
import {
  RETRYABLE_DELAY_INITIAL_TIME,
  RETRYABLE_DELAY_MAX_TIME,
  RETRYABLE_DELAY_JITTER,
  RETRYABLE_RETRY_MAX_TIME_COM,
  RETRYABLE_RETRY_MAX_TIME_OTHERS,
  EVENT_SOURCE_CONNECTING_ERRORS_ALLOWANCE,
  HEARTBEAT_DEFAULT_TIMEOUT,
  HEARTBEAT_CHECK_INTERVAL,
  HEARTBEAT_CHECK_MISS_ALLOWANCE,
} from './constants';
import { ApiClient, ApiClientEvents, ApiClientState } from './ApiClient';
import {
  retryable,
  abortable,
  RetryableBreakError,
  RetryableAbortedError,
  ServiceTicketNotFoundError,
  HTTPError,
} from '../libs';
import { SessionLogoutListenerConfig, SessionLogoutListenerInterface, Jurisdiction } from '../types';

export class SessionLogoutListener implements SessionLogoutListenerInterface {
  private jurisdiction: Jurisdiction;
  private onLogoutCallback: () => void;
  private heartbeatTimeout: number;
  private heartbeatMissedCount: number = 0;
  private heartbeatCheckTimerId: number | null = null;
  private apiClient: ApiClient;
  private casClient: CasClient;
  private eventSource?: EventSource;
  private abortController?: AbortController;

  constructor({
    url,
    heartbeatTimeout = HEARTBEAT_DEFAULT_TIMEOUT,
    jurisdiction,
    onLogoutCallback,
    casClient,
  }: SessionLogoutListenerConfig & {
    jurisdiction: Jurisdiction;
    onLogoutCallback: () => void;
    casClient: CasClient;
  }) {
    this.jurisdiction = jurisdiction;
    this.onLogoutCallback = onLogoutCallback;
    this.heartbeatTimeout = heartbeatTimeout;
    this.apiClient = new ApiClient({ serviceUrl: url });
    this.casClient = casClient;
  }

  public async init(serviceTicket?: string) {
    let shouldRequestServiceTicket = false;

    try {
      this.abortController = new AbortController();

      await Promise.race([
        new Promise((_, reject) => {
          const onAbortCallback = () => {
            this.abortController!.signal.removeEventListener('abort', onAbortCallback);
            reject(new RetryableAbortedError());
          };

          this.abortController!.signal.addEventListener('abort', onAbortCallback);
        }),
        retryable(
          async () => {
            /**
             * Method `close` was invoked and any further calls should be prevented.
             */
            if (this.abortController!.signal.aborted) {
              throw new RetryableAbortedError();
            }

            /**
             * Cleanup possible EventSource instance resources.
             */
            this.dispose();

            let validServiceTicket = serviceTicket;

            /**
             * Attempt to verify Session Listener session existence is happening
             * if one of the following conditions is true:
             * - variable `serviceTicket` value is not provided
             *   That is the case for initialization after direct page launch or page refresh.
             * - local variable `shouldRequestServiceTicket` value is equal to `true`
             *   That is always being set to this value before attempting to call `init`
             *   endpoint of Session Listener.
             *   Previously we were retrying separate calls which could lead to trying to
             *   setup session in Session Listener with expired ServiceTicket and that
             *   change is preventing it from happening as we retry whole flow and
             *   re-fetch new ServiceTicket.
             *   Also additional benefit of retrying whole request flow is that we can
             *   detect if user has been logged out from CAS in which case we will
             *   set state in CAS session manager accordingly.
             */

            if (!validServiceTicket || shouldRequestServiceTicket) {
              /**
               * If there is valid Session Listener session then we
               * just try to subscribe to SSE endpoint for notifications.
               */
              const hasSession = await abortable(() => this.apiClient.verify(), {
                signal: this.abortController!.signal,
              });

              if (hasSession) {
                await abortable(() => this.subscribe(), { signal: this.abortController!.signal });
                return;
              }

              /**
               * Attempt to fetch new ServiceTicket value.
               *
               * If this call fails this means user has been either logged out
               * (no error value provided as CAS client rejects without arguments)
               * or CAS itself returned malformed response.
               *
               * In case this call fails with no error value provided we throw
               * instance of `ServiceTicketNotFoundError` error.
               */
              try {
                const response = await abortable(() => this.casClient.init(), {
                  signal: this.abortController!.signal,
                });
                validServiceTicket = response.serviceTicket;
              } catch (error) {
                if (!error) {
                  throw new ServiceTicketNotFoundError();
                }

                throw error;
              }
            }

            shouldRequestServiceTicket = true;
            await abortable(() => this.apiClient.init(validServiceTicket as string), {
              signal: this.abortController!.signal,
            });
            await abortable(() => this.subscribe(), { signal: this.abortController!.signal });
          },
          {
            delay: {
              initialTime: RETRYABLE_DELAY_INITIAL_TIME,
              maxTime: RETRYABLE_DELAY_MAX_TIME,
              jitter: RETRYABLE_DELAY_JITTER,
            },
            retry: {
              maxTime:
                this.jurisdiction === Jurisdiction.COM ? RETRYABLE_RETRY_MAX_TIME_COM : RETRYABLE_RETRY_MAX_TIME_OTHERS,
              predicate: (error: Error) => {
                if (error instanceof ServiceTicketNotFoundError) {
                  /**
                   * In case of `ServiceTicketNotFoundError` error we need to break
                   * and do not try to retry anymore as there is no CAS session found.
                   */
                  return false;
                }

                if (error instanceof HTTPError) {
                  /**
                   * In case of `HTTPError` error we always want to retry as we check
                   * for response correctness (status code check) on Api Client level
                   * and throw new `HTTPError` error instance if response is not valid.
                   */
                  return true;
                }

                if (error instanceof DOMException && error.name === 'AbortError') {
                  return false;
                }

                return !(error instanceof RetryableBreakError) && !(error instanceof RetryableAbortedError);
              },
            },
          }
        ),
      ]);
    } catch (error) {
      this.close();

      if (error instanceof ServiceTicketNotFoundError || error instanceof RetryableBreakError) {
        this.onLogoutCallback();
      }
    }
  }

  public close() {
    this.dispose();

    if (!this.abortController) {
      return;
    }

    if (!this.abortController.signal.aborted) {
      this.abortController.abort();
    }
  }

  private dispose() {
    if (!this.eventSource) {
      return;
    }

    this.clearHeartbeatCheckTimer();
    this.eventSource.close();
    this.eventSource = undefined;
  }

  private async subscribe() {
    /**
     * Method now is truly async and we listen for `open` and `error` events to check if
     * connection was established properly for initial attempt.
     *
     * For case where user is already subscribed to SSE endpoint there are two cases
     * how retry mechanism can be invoked:
     * - default retry/re-connect mechanism of EventSource
     * - retry after defined amount of missed `HEARTBEAT` events
     */
    return new Promise((resolve, reject) => {
      let eventSourceErrorsCount = 0;

      this.eventSource = this.apiClient.subscribe({
        heartbeatTimeout: this.heartbeatTimeout,
      });
      this.eventSource[ApiClientEvents.OPEN] = () => {
        if (this.eventSource && this.eventSource.readyState === ApiClientState.OPEN) {
          this.eventSource[ApiClientEvents.OPEN] = null;
          this.eventSource[ApiClientEvents.ERROR] = null;
          resolve();
        }
      };
      this.eventSource[ApiClientEvents.ERROR] = () => {
        eventSourceErrorsCount++;

        if (
          this.eventSource &&
          (this.eventSource.readyState === ApiClientState.CLOSED ||
            (this.eventSource.readyState === ApiClientState.CONNECTING &&
              eventSourceErrorsCount >= EVENT_SOURCE_CONNECTING_ERRORS_ALLOWANCE))
        ) {
          this.eventSource[ApiClientEvents.OPEN] = null;
          this.eventSource[ApiClientEvents.ERROR] = null;
          this.dispose();
          reject();
        }
      };
      this.eventSource.addEventListener(ApiClientEvents.INIT, (e) => this.handleEventSourceEvent(e));
      this.eventSource.addEventListener(ApiClientEvents.HEARTBEAT, (e) => this.handleEventSourceEvent(e));
      this.eventSource.addEventListener(ApiClientEvents.LOGOUT, (e) => this.handleEventSourceEvent(e));
    });
  }

  private handleEventSourceEvent({ type }: Event) {
    if (type === ApiClientEvents.INIT) {
      this.clearHeartbeatCheckTimer();
      this.setHeartbeatCheckTimer();
    } else if (type === ApiClientEvents.HEARTBEAT) {
      this.heartbeatMissedCount = 0;
    } else if (type === ApiClientEvents.LOGOUT) {
      this.close();
      this.onLogoutCallback();
    }
  }

  private setHeartbeatCheckTimer() {
    if (this.heartbeatCheckTimerId) {
      return;
    }

    this.heartbeatMissedCount = 0;
    this.heartbeatCheckTimerId = window.setInterval(() => {
      this.heartbeatMissedCount++;

      if (this.heartbeatMissedCount >= HEARTBEAT_CHECK_MISS_ALLOWANCE) {
        this.init();
      }
    }, HEARTBEAT_CHECK_INTERVAL);
  }

  private clearHeartbeatCheckTimer() {
    if (this.heartbeatCheckTimerId) {
      window.clearInterval(this.heartbeatCheckTimerId);
      this.heartbeatCheckTimerId = null;
    }
  }
}
